import React, { useState } from 'react'
import {
  Button,
  FormControl,
  Modal,
  Table,
  InputGroup,
  Alert,
  Spinner,
} from 'react-bootstrap'
import useAdminUsersSearch from 'hooks/use-admin-users-search'
import useCardsForUser from 'hooks/use-cards-for-user'
import useDebounce from 'hooks/use-debounce'
import useMutationTransferProduct from 'hooks/use-mutation-transfer-product'
import { LinqProduct } from 'interfaces/linq-product'
import { Card } from 'interfaces/card'
import { User } from 'interfaces/user'

const TransferProductToUserModal = ({
  show,
  product,
  oldUser,
  handleClose,
  userUUID,
}: {
  show: boolean
  product: LinqProduct
  oldUser: User | null
  handleClose: () => void
  userUUID: string
}) => {
  const [query, setQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQuery(event.target.value)
  }

  const transferProductMutation = useMutationTransferProduct({ userUUID })

  const debouncedQuery = useDebounce(query, 500)
  const { data, isFetching } = useAdminUsersSearch(debouncedQuery)
  const users = data?.users

  const { data: cardsData, isFetching: cardsFetching } = useCardsForUser(
    selectedUser?.uuid || ''
  )
  const cards = cardsData?.cards

  const handleSelectUser = (user: User) => {
    setSelectedUser(user)
  }

  const handleBack = () => {
    setSelectedUser(null)
  }

  const handleTransferToCard = (card: Card) => {
    if (
      window.confirm(
        `Transfer '${product?.nick_name}' from '${oldUser?.name}' to '${
          selectedUser?.name
        }' on page '${card.name || card.connecting_code}'?`
      )
    ) {
      transferProductMutation.mutate(
        {
          productId: product.id,
          recipientUserUuid: selectedUser?.uuid || '',
          cardCode: card.connecting_code,
        },
        {
          onSuccess: () => {
            setSelectedUser(null)
            handleClose()
          },
        }
      )
    }
  }

  const onClose = () => {
    setSelectedUser(null)
    handleClose()
  }

  return (
    <Modal size="lg" show={show} onHide={onClose}>
      <Modal.Header>
        <Modal.Title>
          {selectedUser ? (
            <>Select a page for &apos;{selectedUser.name}&apos;</>
          ) : (
            <>
              Transfer &apos;{product?.nick_name}&apos; from &apos;
              {oldUser?.name}&apos;
            </>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-3"
              onClick={handleBack}
            >
              &larr; Back to user search
            </Button>
            {cardsFetching ? (
              <div className="text-center p-4">
                <Spinner animation="border" size="sm" /> Loading pages...
              </div>
            ) : cards && cards.length > 0 ? (
              <Table striped bordered hover responsive variant="dark">
                <thead>
                  <tr>
                    <th>Page Name</th>
                    <th>Code</th>
                    <th>Transfer</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card: Card) => (
                    <tr key={card.id}>
                      <td>{card.name || 'Untitled'}</td>
                      <td>
                        <code>/{card.connecting_code}</code>
                      </td>
                      <td>
                        <Button
                          variant="success"
                          size="sm"
                          disabled={transferProductMutation.isLoading}
                          onClick={() => handleTransferToCard(card)}
                        >
                          Transfer here
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="warning">
                This user has no pages. Cannot transfer product.
              </Alert>
            )}
          </>
        ) : (
          <>
            <InputGroup className="mb-4 mt-4">
              <FormControl
                autoComplete="off"
                onChange={handleSearchInputChange}
                value={query}
                type="text"
                placeholder="Search by name, email, phone, or /page_url"
                aria-label="Search Users"
              />
            </InputGroup>
            <Table striped bordered hover responsive variant="dark">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Select</th>
                </tr>
              </thead>
              <tbody>
                {isFetching ? (
                  <tr>
                    <td colSpan={5}>Searching...</td>
                  </tr>
                ) : (
                  <>
                    {users?.map((user: User) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.phone_number}</td>
                        <td>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleSelectUser(user)}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default TransferProductToUserModal
